<template>
    <div class="amazonKey-details-main-container" v-if="!loading">
        <div class="details-container">
            <div class="form-container">
                <div class="title-wrapper">
                    <p>
                        {{ $t("amazonKey.details.activate.title") }}
                    </p>
                    <div class="amazonKey-toggle-wrapper">
                        <BasicToggle
                            v-model="amazonKeyInfos.state"
                            @update:modelValue="toggleActivate"
                        />
                        <p>
                            {{
                                activate
                                    ? $t("amazonKey.details.activate.toggleOff")
                                    : $t("amazonKey.details.activate.toggleOn")
                            }}
                        </p>
                    </div>
                </div>
                <div class="form-wrapper" v-if="amazonKeyInfos.state">
                    <div v-if="countryMissingAlert" class="country-missing">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        <p>
                            {{ $t("amazonKey.details.form.errors.countryId") }}
                        </p>
                    </div>
                    <div class="form-field">
                        <p>{{ $t("residencesList.arrayColumns.name") }}*</p>
                        <BasicInput
                            maxlength="128"
                            name="amazonKey-name"
                            v-model="amazonKeyInfos.name"
                        />
                    </div>
                    <div class="form-field">
                        <p>{{ $t("residencesList.form.country") }}*</p>
                        <BasicDropdown
                            v-model="country"
                            :options="countryList"
                            :neededPlaceholder="true"
                            :disabled="[0]"
                            name="amazonKey-country"
                        />
                    </div>
                    <div class="form-field">
                        <p>{{ $t("residencesList.form.address") }}*</p>
                        <BasicInput
                            name="amazonKey-address"
                            v-model="amazonKeyInfos.address"
                        />
                    </div>
                    <div class="form-field">
                        <p>
                            {{ $t("residencesList.form.postal") }} /
                            {{ $t("residencesList.form.city") }}*
                        </p>
                        <div class="postal-code">
                            <BasicInput
                                name="amazonKey-postalCode"
                                v-model="amazonKeyInfos.postalCode"
                            />
                            <BasicInput
                                name="amazonKey-city"
                                v-model="amazonKeyInfos.city"
                            />
                        </div>
                    </div>
                    <div class="form-field">
                        <p>
                            {{ $t("amazonKey.details.form.picture") }}
                        </p>
                        <BasicDropFile
                            v-if="!amazonKeyInfos.imageUrl"
                            :allowedTypes="['jpeg', 'png']"
                            @changeFile="changeFile"
                        />
                        <div v-else class="amazonKey-file">
                            <p @click="openLink(amazonKeyInfos.imageUrl)">
                                {{ $t("amazonKey.details.form.checkPicture") }}
                            </p>
                            <i
                                class="fa-solid fa-circle-xmark"
                                @click="changeFile(false)"
                            ></i>
                        </div>
                    </div>
                </div>
                <div class="button-wrapper">
                    <BasicButton
                        @click="
                            canBeSaved || needToChangeImage
                                ? saveMethods()
                                : null
                        "
                        :color="
                            canBeSaved || needToChangeImage ? 'orange' : 'grey'
                        "
                        :disabled="!canBeSaved && !needToChangeImage"
                    >
                        {{ $t("button.register") }}
                    </BasicButton>
                    <BasicButton color="blue" @click="goBack">{{
                        $t("button.cancel")
                    }}</BasicButton>
                </div>
            </div>
            <div class="map-wrapper" v-if="amazonKeyInfos.state">
                <BasicMap
                    :mapCenter="mapCoordinates"
                    :mapZoom="16"
                    :displayIcon="true"
                    :iconUrl="getIconUrl"
                ></BasicMap>
            </div>
        </div>
    </div>
    <div v-else class="loader">
        <Loader />
    </div>
</template>

<script>
import { debounce as _debounce } from "lodash"

import {
    getAmazonKey,
    activateOrUpdateAmazonKey,
    deactivateAmazonKey,
    addAmazonKeyImage,
    deleteAmazonKeyImage,
} from "@/services/intratone/amazonKey"
import { getGeocodingSearch } from "@/services/intratone/geocoding"
import { getMiscellaneous } from "@/services/intratone/miscellaneous"

import { imageFileToBlob } from "@/helpers/commonFunctions"
import { useToast } from "vue-toastification"

import BasicToggle from "@/components/basic/BasicToggle.vue"
import BasicInput from "@/components/basic/BasicInput.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import BasicDropdown from "@/components/basic/BasicDropdown.vue"
import BasicDropFile from "@/components/basic/BasicDropFile.vue"
import BasicMap from "@/components/basic/BasicMap.vue"
import Loader from "@/components/basic/Loader.vue"

const toast = useToast()

export default {
    components: {
        BasicToggle,
        BasicInput,
        BasicButton,
        BasicDropdown,
        BasicDropFile,
        BasicMap,
        Loader,
    },
    emits: ["update:canBeSaved"],
    data() {
        return {
            loading: false,
            activate: false,
            amazonKeyOriginalInfos: {},
            amazonKeyInfos: {
                id: null,
                name: null,
                accessId: null,
                address: null,
                city: null,
                postalCode: null,
                region: null,
                state: null,
                image: null,
            },
            mapCoordinates: { lat: 50.05, lon: 1.416667 },
            country: {
                name: null,
                value: null,
            },
            countryList: [
                {
                    name: this.$t("register.chooseCountry"),
                    value: 0,
                },
            ],
            countryMissingAlert: false,
        }
    },

    watch: {
        amazonKeyInfos: {
            handler(value) {
                if (value.city && value.address && value.postalCode) {
                    this.retrieveCoordinates()
                }
            },
            deep: true,
        },
    },

    computed: {
        getIconUrl() {
            return require("@/assets/images/others/pin-location.svg")
        },

        // Check if any data has changed and if all required data are set
        canBeSaved() {
            const result = !!(
                // Name
                (
                    (this.amazonKeyInfos.name?.trim() !==
                        this.amazonKeyOriginalInfos.name ||
                        // Address
                        this.amazonKeyInfos.address?.trim() !==
                            this.amazonKeyOriginalInfos.address ||
                        // Postal Code
                        this.amazonKeyInfos.postalCode?.trim() !==
                            this.amazonKeyOriginalInfos.postal_code ||
                        // // City
                        this.amazonKeyInfos.city?.trim() !==
                            this.amazonKeyOriginalInfos.city ||
                        // // State
                        this.amazonKeyInfos.state !==
                            this.amazonKeyOriginalInfos.state ||
                        parseInt(this.country?.value) !==
                            parseInt(this.amazonKeyOriginalInfos.pays_id)) &&
                    this.amazonKeyInfos.name?.trim() &&
                    this.amazonKeyInfos.address?.trim() &&
                    this.amazonKeyInfos.postalCode?.trim() &&
                    this.amazonKeyInfos.city?.trim() &&
                    parseInt(this.country.value)
                )
            )
            this.$emit("update:canBeSaved", result)
            return result
        },

        needToChangeImage() {
            return (
                this.amazonKeyInfos.image !==
                this.amazonKeyOriginalInfos.img_url
            )
        },

        countriesStore() {
            return this.$store.getters["miscellaneous/getAllCountries"]
        },
    },

    async mounted() {
        this.loading = true
        await this.getCountries()
        await this.retrieveAmazonKey()
        this.loading = false
    },

    methods: {
        // Toggle On or Off for Amazon Key
        async toggleActivate(value) {
            this.amazonKeyInfos.state = value

            // If it's first time that Amazon Key is active, and if there is no country registered on parent, show message
            if (this.amazonKeyOriginalInfos.pays_id == 0 && value) {
                this.countryMissingAlert = true
            }
        },

        attributesValue(data) {
            this.amazonKeyInfos = {
                accessId: data.access_id,
                name: data.name,
                address: data.address,
                city: data.city,
                postalCode: data.postal_code,
                region: data.region,
                state: data.state === "ACTIVATE",
                image: data.img_url ? true : false,
                imageUrl: data.img_url,
            }

            this.amazonKeyOriginalInfos = data

            this.amazonKeyOriginalInfos.img_url = data.img_url ? true : false

            this.amazonKeyOriginalInfos.state =
                this.amazonKeyOriginalInfos.state === "ACTIVATE"
        },

        resetDataFields() {
            const data = this.amazonKeyOriginalInfos

            this.amazonKeyInfos = {
                accessId: data.access_id,
                id: data.id,
                name: data.name,
                address: data.address,
                city: data.city,
                postalCode: data.postal_code,
                region: data.region,
                state: data.state === "ACTIVATE",
                image: data.img_url ? true : false,
            }
        },

        // Get Amazon Key infos
        async retrieveAmazonKey() {
            this.loading = true
            await getAmazonKey(this.$route.params.accessId)
                .then((response) => {
                    if (response.data) {
                        // Set data
                        this.attributesValue(response.data)

                        // Find country corresponding to the ID
                        this.country = this.countryList.find((item) => {
                            return (
                                parseInt(item.value) ===
                                parseInt(response.data.pays_id)
                            )
                        })
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },

        // Save Amazon Key
        async saveMethods() {
            this.loading = true
            // If we deactivate it
            if (this.amazonKeyInfos.state === false) {
                await deactivateAmazonKey(this.$route.params.accessId)
                    .then((response) => {
                        if (!response.error) {
                            toast.success(
                                this.$t("alerts.success.savedChanges")
                            )

                            this.amazonKeyOriginalInfos.state = false
                            this.resetDataFields()
                        } else {
                            toast.error(
                                this.$t("alerts.amazonKey.errors.update")
                            )
                        }
                    })
                    .catch(() => {
                        toast.error(this.$t("alerts.amazonKey.errors.update"))
                    })
            }
            // If we activate it
            else {
                let successAlert = false
                if (this.needToChangeImage) {
                    if (this.amazonKeyInfos.image) {
                        await addAmazonKeyImage(this.amazonKeyInfos.accessId, {
                            image: this.amazonKeyInfos.image,
                        })
                            .then(async (response) => {
                                if (!response.error) {
                                    this.amazonKeyOriginalInfos.img_url = true

                                    successAlert = true

                                    await this.retrieveAmazonKey()
                                } else {
                                    toast.error(
                                        this.$t("alerts.amazonKey.errors.image")
                                    )
                                }
                            })
                            .catch(() => {
                                toast.error(
                                    this.$t("alerts.amazonKey.errors.image")
                                )
                            })
                    } else {
                        await deleteAmazonKeyImage(this.amazonKeyInfos.accessId)
                            .then((response) => {
                                if (!response.error) {
                                    this.amazonKeyOriginalInfos.img_url = false

                                    successAlert = true
                                } else {
                                    toast.error(
                                        this.$t("alerts.amazonKey.errors.image")
                                    )
                                }
                            })
                            .catch(() => {
                                toast.error(
                                    this.$t("alerts.amazonKey.errors.image")
                                )
                            })
                    }
                }

                if (this.canBeSaved) {
                    const params = {
                        name: this.amazonKeyInfos.name.trim(),
                        address: this.amazonKeyInfos.address.trim(),
                        country_id: parseInt(this.country?.value),
                        postal_code: this.amazonKeyInfos.postalCode.trim(),
                        city: this.amazonKeyInfos.city.trim(),
                        state: this.amazonKeyInfos.region
                            ? this.amazonKeyInfos.region
                            : this.amazonKeyInfos.city,
                    }

                    await activateOrUpdateAmazonKey(
                        this.$route.params.accessId,
                        params
                    )
                        .then((response) => {
                            if (response.data && !response.error) {
                                this.countryMissingAlert = false

                                this.amazonKeyOriginalInfos.state =
                                    response.data.state === "ACTIVATE"

                                this.attributesValue(response.data)

                                successAlert = true
                            } else {
                                toast.error(
                                    this.$t("alerts.amazonKey.errors.update")
                                )
                            }
                        })
                        .catch(() => {
                            toast.error(
                                this.$t("alerts.amazonKey.errors.update")
                            )
                        })
                }

                if (successAlert) {
                    toast.success(this.$t("alerts.success.savedChanges"))
                }
            }

            this.loading = false
        },

        async changeFile(value) {
            if (value) {
                this.amazonKeyInfos.image = await imageFileToBlob(value)
            } else {
                this.amazonKeyInfos.image = false
                this.amazonKeyInfos.imageUrl = false
            }
        },

        // Find coordinates with Address, City and Postal Code. Add debounce to prevent API spam
        retrieveCoordinates: _debounce(async function () {
            const search = `${this.amazonKeyInfos.address}, ${this.amazonKeyInfos.city}, ${this.amazonKeyInfos.postalCode}`
            await getGeocodingSearch({ q: search }).then((response) => {
                // If there is some result
                if (response.data.list.length > 0) {
                    this.mapCoordinates = {
                        lat: response.data.list[0].lat.toString(),
                        lon: response.data.list[0].lon.toString(),
                    }

                    this.amazonKeyInfos.region = response.data.list[0].state
                }
                // Set default value
                else {
                    this.mapCoordinates = { lat: 50.05, lon: 1.416667 }
                }
            })
        }, 750),

        async getCountries() {
            const params = {
                entities: ["countries"],
                lng: this.$i18n.locale,
            }

            if (this.countriesStore.length === 0) {
                await getMiscellaneous(params).then((response) => {
                    let items = response.data.list.countries

                    items.forEach((item) => {
                        this.countryList.push({
                            name: item.translate,
                            value: parseInt(item.id),
                        })
                    })

                    this.$store.commit(
                        "miscellaneous/setCountries",
                        response.data.list.countries
                    )
                })
            } else {
                this.countriesStore.forEach((item) => {
                    this.countryList.push({
                        name: item.translate,
                        value: parseInt(item.id),
                    })
                })
            }
        },

        goBack() {
            this.$router.go(-1)
        },

        openLink(link) {
            window.open(link, "_blank")
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";
h1,
p {
    margin: 0;
}

.amazonKey-details-main-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1em;

    .details-container {
        width: 100%;
        height: 100%;
        display: flex;

        .form-container {
            width: 100%;
            max-width: 700px;
            height: 100%;
            max-height: 100%;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 2em;
            padding-right: 2em;

            .title-wrapper {
                display: flex;
                flex-direction: column;
                gap: 0.5em;

                .amazonKey-toggle-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 1em;
                }
            }

            .country-missing {
                display: flex;
                align-items: center;
                gap: 0.5em;
                padding: 1em;
                border: 1px solid $orange-neutral;
                border-radius: 10px;

                i {
                    color: $orange-neutral;
                }
            }

            .form-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                gap: 1em;
                padding-bottom: 1em;

                .form-field {
                    width: 100%;
                    display: flex;
                    align-items: start;
                    gap: 1em;

                    @media all and (max-width: 768px) {
                        flex-direction: column;
                        gap: 0.5em;
                    }

                    p {
                        width: 40%;

                        @media all and (max-width: 768px) {
                            width: 100%;
                        }
                    }

                    .postal-code {
                        width: 100%;
                        display: flex;
                        gap: 1em;
                    }

                    .amazonKey-file {
                        width: 100%;
                        display: flex;
                        align-items: center;
                        gap: 10px;

                        p {
                            transition: all ease-in-out 0.1s;
                            width: max-content;
                            border-bottom: 1px solid transparent;

                            &:hover {
                                cursor: pointer;
                                border-color: $blue-neutral;
                                transition: all ease-in-out 0.1s;
                            }
                        }

                        i {
                            transition: all ease-in-out 0.1s;
                            cursor: pointer;

                            &:hover {
                                transition: all ease-in-out 0.1s;
                                transform: scale(1.1);
                            }
                        }
                    }

                    :deep(.basic-input) {
                        width: 100%;
                    }
                }
            }

            .button-wrapper {
                display: flex;
                gap: 1em;
            }
        }

        .map-wrapper {
            width: 100%;
            max-width: 500px;
            max-height: 510px;

            @media all and (max-width: 1024px) {
                display: none;
            }
        }
    }
}
</style>
